import { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { useAuth } from '../context/AuthContext';
import { adminApi, orderService, API_ORIGIN } from '../services/api';
import type { Product, AdminOrder, ProductInput, CustomerSummary, CustomerDetail, ProductBuyer, StoreSettings, SubscriberSummary, FeedbackEntry } from '../types';
import { formatCentsUSD, dollarsToCents, centsToDollars } from '../lib/money';
import { PRICING_CONFIG_UPDATED_EVENT } from '../hooks/usePricingConfig';
import {
  Loader2, Package, ClipboardList, Users, Plus, Pencil, Archive, ArchiveRestore, Upload, Download,
  X, Image as ImageIcon, ChevronDown, ChevronRight, ShieldAlert, Search,
  UserCircle, MapPin, ShoppingBag, Settings as SettingsIcon, RefreshCw, RotateCcw, Mail,
  MessageSquareHeart,
} from 'lucide-react';

const CATEGORIES: ProductInput['category'][] = ['crunch', 'granola', 'blends', 'other'];

const ORDER_STATUSES = ['pending', 'paid', 'shipped', 'delivered', 'failed'] as const;
type OrderStatus = (typeof ORDER_STATUSES)[number];

const STATUS_STYLES: Record<string, string> = {
  pending: 'bg-yellow-100 text-yellow-800',
  paid: 'bg-blue-100 text-blue-800',
  shipped: 'bg-purple-100 text-purple-800',
  delivered: 'bg-green-100 text-green-800',
  failed: 'bg-red-100 text-red-800',
  refunded: 'bg-gray-200 text-gray-800',
};

function resolveImageUrl(image: string): string {
  if (!image) return '';
  if (/^https?:\/\//.test(image) || image.startsWith('data:')) return image;
  if (image.startsWith('/uploads/')) return `${API_ORIGIN}${image}`;
  return image;
}

// Formats integer cents USD for display (e.g. 1299 -> "$12.99").
function money(centsUSD: number): string {
  return formatCentsUSD(centsUSD);
}

function fmtDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString();
}

function StatusBadge({ status }: { status: string }) {
  return (
    <span className={`px-2.5 py-1 rounded-full text-xs font-medium capitalize ${STATUS_STYLES[status] ?? 'bg-gray-100 text-gray-700'}`}>
      {status}
    </span>
  );
}

const EMPTY_FORM: ProductInput & { id?: string } = {
  name: '',
  description: '',
  priceCentsUSD: 0,
  image: '',
  category: 'crunch',
  isOrganic: true,
  inStock: true,
  // null = inherit the store default per-item shipping fee. New items start unset.
  shippingCentsUSD: null,
};

/* ------------------------------- Product editor modal ------------------------------- */

function ProductModal({
  initial, isNew, token, onClose, onSaved,
}: {
  initial: (ProductInput & { id?: string }) | null;
  isNew: boolean;
  token: string;
  onClose: () => void;
  onSaved: (product: Product) => void;
}) {
  const [form, setForm] = useState<ProductInput & { id?: string }>(initial ?? EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const fileRef = useRef<HTMLInputElement>(null);

  function set<K extends keyof typeof form>(key: K, value: (typeof form)[K]) {
    setForm(prev => ({ ...prev, [key]: value }));
  }

  async function handleUpload(file: File) {
    setUploading(true);
    setError('');
    try {
      const dataUrl = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = () => reject(new Error('Could not read file'));
        reader.readAsDataURL(file);
      });
      const { url } = await adminApi.uploadImage(token, dataUrl, file.name);
      set('image', url);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setUploading(false);
    }
  }

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    setSaving(true);
    try {
      const payload: ProductInput = {
        name: form.name.trim(),
        description: form.description.trim(),
        priceCentsUSD: Number(form.priceCentsUSD),
        image: form.image.trim(),
        category: form.category,
        isOrganic: form.isOrganic,
        inStock: form.inStock,
        // null = inherit the store default; a number (incl. 0) is an override.
        shippingCentsUSD: form.shippingCentsUSD == null ? null : Number(form.shippingCentsUSD) || 0,
      };
      const saved = isNew
        ? await adminApi.createProduct(token, { ...payload, id: form.id?.trim() || undefined })
        : await adminApi.updateProduct(token, form.id!, payload);
      onSaved(saved);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  const preview = resolveImageUrl(form.image);

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl my-8">
        <div className="flex items-center justify-between border-b px-6 py-4">
          <h2 className="text-xl font-serif font-bold text-coastal-700">
            {isNew ? 'Add Product' : 'Edit Product'}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700" aria-label="Close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSave} className="px-6 py-5 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
            <input value={form.name} onChange={e => set('name', e.target.value)}
              className="w-full px-3 py-2 border rounded-lg" required maxLength={200} />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea value={form.description} onChange={e => set('description', e.target.value)}
              className="w-full px-3 py-2 border rounded-lg" rows={3} required maxLength={2000} />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Price (USD)</label>
              <input type="number" step="0.01" min="0.01" value={centsToDollars(form.priceCentsUSD)}
                onChange={e => set('priceCentsUSD', dollarsToCents(e.target.valueAsNumber || 0))}
                className="w-full px-3 py-2 border rounded-lg" required />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
              <select value={form.category} onChange={e => set('category', e.target.value as ProductInput['category'])}
                className="w-full px-3 py-2 border rounded-lg bg-white">
                {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Shipping (per unit)</label>
            <label className="flex items-center gap-2 text-sm text-gray-700 mb-2">
              <input type="checkbox" checked={form.shippingCentsUSD == null}
                onChange={e => set('shippingCentsUSD', e.target.checked ? null : 0)} />
              Use the store default shipping fee
            </label>
            {form.shippingCentsUSD != null && (
              <>
                <input type="number" step="0.01" min="0" value={centsToDollars(form.shippingCentsUSD)}
                  onChange={e => set('shippingCentsUSD', dollarsToCents(e.target.valueAsNumber || 0))}
                  className="w-full px-3 py-2 border rounded-lg" />
                <p className="text-xs text-gray-500 mt-1">
                  Per-unit override for this item (set 0 to ship this item free). Free-shipping orders waive it.
                </p>
              </>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Image</label>
            <div className="flex items-start gap-4">
              <div className="w-24 h-24 rounded-lg border bg-gray-50 flex items-center justify-center overflow-hidden flex-shrink-0">
                {preview ? (
                  <img src={preview} alt="Preview" className="w-full h-full object-cover" />
                ) : (
                  <ImageIcon className="w-8 h-8 text-gray-300" />
                )}
              </div>
              <div className="flex-1 space-y-2">
                <input value={form.image} onChange={e => set('image', e.target.value)}
                  placeholder="Image URL or /uploads/... path"
                  className="w-full px-3 py-2 border rounded-lg text-sm" />
                <input ref={fileRef} type="file" accept="image/*" className="hidden"
                  onChange={e => { const f = e.target.files?.[0]; if (f) handleUpload(f); e.target.value = ''; }} />
                <Button type="button" variant="outline" size="sm" disabled={uploading}
                  onClick={() => fileRef.current?.click()} className="flex items-center gap-2">
                  {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                  {uploading ? 'Uploading...' : 'Upload image'}
                </Button>
              </div>
            </div>
          </div>

          {isNew && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Custom ID <span className="text-gray-400 font-normal">(optional — auto-generated if blank)</span>
              </label>
              <input value={form.id ?? ''} onChange={e => set('id', e.target.value)}
                className="w-full px-3 py-2 border rounded-lg" maxLength={100} />
            </div>
          )}

          <div className="flex gap-6">
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input type="checkbox" checked={form.isOrganic} onChange={e => set('isOrganic', e.target.checked)} />
              All Natural
            </label>
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input type="checkbox" checked={form.inStock} onChange={e => set('inStock', e.target.checked)} />
              In Stock
            </label>
          </div>

          {error && <p className="text-red-600 text-sm">{error}</p>}

          <div className="flex justify-end gap-3 pt-2">
            <Button type="button" variant="outline" size="sm" onClick={onClose}>Cancel</Button>
            <Button type="submit" size="sm" disabled={saving} className="flex items-center gap-2">
              {saving && <Loader2 className="w-4 h-4 animate-spin" />}
              {isNew ? 'Create' : 'Save changes'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

/* ------------------------------- Products tab ------------------------------- */

function ProductsTab({ token, notify, onViewCustomer }: {
  token: string;
  notify: (msg: string) => void;
  onViewCustomer: (email: string) => void;
}) {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState<'active' | 'archived'>('active');
  const [modal, setModal] = useState<{ initial: (ProductInput & { id?: string }) | null; isNew: boolean } | null>(null);
  const [importing, setImporting] = useState(false);
  const [importErrors, setImportErrors] = useState<{ row: number; message: string }[]>([]);
  const [buyersFor, setBuyersFor] = useState<Product | null>(null);
  const importRef = useRef<HTMLInputElement>(null);

  const load = useCallback(() => {
    setLoading(true);
    adminApi.getProducts(token).then(setProducts).catch(console.error).finally(() => setLoading(false));
  }, [token]);

  useEffect(load, [load]);

  const activeProducts = products.filter(p => !p.archived);
  const archivedProducts = products.filter(p => p.archived);
  const visibleProducts = view === 'active' ? activeProducts : archivedProducts;

  function openEdit(p: Product) {
    const initial: ProductInput & { id?: string } = {
      id: p.id,
      name: p.name,
      description: p.description,
      priceCentsUSD: p.priceCentsUSD,
      image: p.image,
      category: p.category,
      isOrganic: p.isOrganic,
      inStock: p.inStock,
      shippingCentsUSD: p.shippingCentsUSD,
    };
    setModal({ isNew: false, initial });
  }

  async function handleArchive(p: Product) {
    if (!confirm(`Archive "${p.name}"? It will be hidden from the storefront but kept for order history. You can restore it anytime.`)) return;
    try {
      const { product } = await adminApi.archiveProduct(token, p.id);
      setProducts(prev => prev.map(x => (x.id === product.id ? product : x)));
      notify(`Archived ${p.name}`);
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Archive failed');
    }
  }

  async function handleRestore(p: Product) {
    try {
      const { product } = await adminApi.unarchiveProduct(token, p.id);
      setProducts(prev => prev.map(x => (x.id === product.id ? product : x)));
      notify(`Restored ${p.name}`);
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Restore failed');
    }
  }

  async function handleExport() {
    try {
      const csv = await adminApi.exportProducts(token);
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'products-export.csv';
      a.click();
      URL.revokeObjectURL(url);
      notify('Exported products to CSV');
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Export failed');
    }
  }

  async function handleImport(file: File) {
    setImporting(true);
    setImportErrors([]);
    try {
      const csv = await file.text();
      const result = await adminApi.importProducts(token, csv);
      notify(`Import complete: ${result.created} created, ${result.updated} updated`);
      load();
    } catch (err) {
      const e = err as Error & { rowErrors?: { row: number; message: string }[] };
      if (e.rowErrors?.length) setImportErrors(e.rowErrors);
      alert(e.message || 'Import failed');
    } finally {
      setImporting(false);
    }
  }

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div className="flex gap-2">
          <button onClick={() => setView('active')}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
              view === 'active' ? 'bg-coastal-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>
            Active ({activeProducts.length})
          </button>
          <button onClick={() => setView('archived')}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
              view === 'archived' ? 'bg-coastal-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>
            Archived ({archivedProducts.length})
          </button>
        </div>
        <div className="flex flex-wrap gap-2">
          <input ref={importRef} type="file" accept=".csv,text/csv" className="hidden"
            onChange={e => { const f = e.target.files?.[0]; if (f) handleImport(f); e.target.value = ''; }} />
          <Button variant="outline" size="sm" disabled={importing} onClick={() => importRef.current?.click()}
            className="flex items-center gap-2">
            {importing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Import CSV
          </Button>
          <Button variant="outline" size="sm" onClick={handleExport} className="flex items-center gap-2">
            <Download className="w-4 h-4" /> Export CSV
          </Button>
          <Button size="sm" onClick={() => setModal({ isNew: true, initial: null })} className="flex items-center gap-2">
            <Plus className="w-4 h-4" /> Add Product
          </Button>
        </div>
      </div>

      {importErrors.length > 0 && (
        <div className="mb-5 rounded-lg border border-red-200 bg-red-50 p-4">
          <p className="font-medium text-red-800 mb-2">Import rejected — fix these rows and try again:</p>
          <ul className="text-sm text-red-700 list-disc pl-5 space-y-0.5">
            {importErrors.map((e, i) => <li key={i}>Row {e.row}: {e.message}</li>)}
          </ul>
        </div>
      )}

      {loading ? (
        <div className="text-center py-16">
          <Loader2 className="w-8 h-8 text-coastal-600 animate-spin mx-auto" />
        </div>
      ) : (
        <Card className="p-0 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 text-left text-gray-500 uppercase text-xs tracking-wide">
                  <th className="px-4 py-3 font-medium">Image</th>
                  <th className="px-4 py-3 font-medium">Name</th>
                  <th className="px-4 py-3 font-medium">Category</th>
                  <th className="px-4 py-3 font-medium">Price</th>
                  <th className="px-4 py-3 font-medium">Stock</th>
                  <th className="px-4 py-3 font-medium text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {visibleProducts.map(p => {
                  const img = resolveImageUrl(p.image);
                  return (
                    <tr key={p.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <div className="w-12 h-12 rounded border bg-gray-50 flex items-center justify-center overflow-hidden">
                          {img ? <img src={img} alt={p.name} className="w-full h-full object-cover" />
                            : <ImageIcon className="w-5 h-5 text-gray-300" />}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <p className="font-medium text-gray-900">{p.name}</p>
                        <p className="text-gray-400 font-mono text-xs">{p.id}</p>
                      </td>
                      <td className="px-4 py-3 capitalize text-gray-600">{p.category}</td>
                      <td className="px-4 py-3 text-gray-900">{money(p.priceCentsUSD)}</td>
                      <td className="px-4 py-3">
                        {p.archived ? (
                          <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">Archived</span>
                        ) : (
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${p.inStock ? 'bg-green-100 text-green-800' : 'bg-gray-200 text-gray-600'}`}>
                            {p.inStock ? 'In stock' : 'Out'}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex justify-end gap-2">
                          <button onClick={() => setBuyersFor(p)} className="text-gray-500 hover:text-coastal-700 p-1.5" aria-label="View buyers" title="View buyers">
                            <Users className="w-4 h-4" />
                          </button>
                          <button onClick={() => openEdit(p)} className="text-coastal-600 hover:text-coastal-800 p-1.5" aria-label="Edit" title="Edit">
                            <Pencil className="w-4 h-4" />
                          </button>
                          {p.archived ? (
                            <button onClick={() => handleRestore(p)} className="text-green-600 hover:text-green-800 p-1.5" aria-label="Restore" title="Restore to storefront">
                              <ArchiveRestore className="w-4 h-4" />
                            </button>
                          ) : (
                            <button onClick={() => handleArchive(p)} className="text-amber-600 hover:text-amber-800 p-1.5" aria-label="Archive" title="Archive (hide from storefront)">
                              <Archive className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {visibleProducts.length === 0 && (
                  <tr><td colSpan={6} className="px-4 py-12 text-center text-gray-400">
                    {view === 'archived' ? 'No archived products.' : 'No products yet.'}
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {modal && (
        <ProductModal
          initial={modal.initial}
          isNew={modal.isNew}
          token={token}
          onClose={() => setModal(null)}
          onSaved={(saved) => {
            setProducts(prev => {
              const exists = prev.some(p => p.id === saved.id);
              return exists ? prev.map(p => (p.id === saved.id ? saved : p)) : [...prev, saved];
            });
            notify(modal.isNew ? `Created ${saved.name}` : `Updated ${saved.name}`);
            setModal(null);
          }}
        />
      )}

      {buyersFor && (
        <ProductBuyersModal
          token={token}
          product={buyersFor}
          onClose={() => setBuyersFor(null)}
          onViewCustomer={(email) => { setBuyersFor(null); onViewCustomer(email); }}
        />
      )}
    </div>
  );
}

/* ------------------------------- Product buyers modal ------------------------------- */

function ProductBuyersModal({ token, product, onClose, onViewCustomer }: {
  token: string;
  product: Product;
  onClose: () => void;
  onViewCustomer: (email: string) => void;
}) {
  const [buyers, setBuyers] = useState<ProductBuyer[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    adminApi.getProductBuyers(token, product.id)
      .then(r => setBuyers(r.buyers))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [token, product.id]);

  const totalUnits = buyers.reduce((s, b) => s + b.quantity, 0);
  const uniqueCustomers = new Set(buyers.map(b => b.customerEmail.toLowerCase())).size;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl my-8">
        <div className="flex items-center justify-between border-b px-6 py-4">
          <div>
            <h2 className="text-xl font-serif font-bold text-coastal-700">Buyers of {product.name}</h2>
            <p className="text-sm text-gray-500">
              {loading ? 'Loading…' : `${uniqueCustomers} customer${uniqueCustomers !== 1 ? 's' : ''} · ${totalUnits} unit${totalUnits !== 1 ? 's' : ''} across ${buyers.length} order${buyers.length !== 1 ? 's' : ''}`}
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700" aria-label="Close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-6 py-4 max-h-[70vh] overflow-y-auto">
          {loading ? (
            <div className="text-center py-12"><Loader2 className="w-8 h-8 text-coastal-600 animate-spin mx-auto" /></div>
          ) : buyers.length === 0 ? (
            <p className="text-center py-12 text-gray-400">No one has ordered this product yet.</p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-gray-500 uppercase text-xs tracking-wide border-b">
                  <th className="py-2 font-medium">Customer</th>
                  <th className="py-2 font-medium">Qty</th>
                  <th className="py-2 font-medium">Unit</th>
                  <th className="py-2 font-medium">Date</th>
                  <th className="py-2 font-medium">Order status</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {buyers.map((b, i) => (
                  <tr key={b.orderId + i} className="hover:bg-gray-50">
                    <td className="py-2.5">
                      <button onClick={() => onViewCustomer(b.customerEmail)}
                        className="text-left group">
                        <span className="font-medium text-gray-900 group-hover:text-coastal-700">{b.customerName || '—'}</span>
                        <span className="block text-xs text-gray-500 group-hover:underline">{b.customerEmail}</span>
                      </button>
                      {!b.hasAccount && <span className="ml-1 text-[10px] uppercase tracking-wide text-gray-400">guest</span>}
                    </td>
                    <td className="py-2.5 text-gray-700">{b.quantity}</td>
                    <td className="py-2.5 text-gray-700">{money(b.unitPriceCentsUSD)}</td>
                    <td className="py-2.5 text-gray-600 whitespace-nowrap">{fmtDate(b.createdAt)}</td>
                    <td className="py-2.5"><StatusBadge status={b.status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

/* ------------------------------- Orders tab ------------------------------- */

// Shared state + mutation handlers for an order, used by both the desktop table
// row and the mobile card so the two layouts stay in sync behaviorally.
function useOrderRow(
  order: AdminOrder,
  token: string,
  onUpdated: (o: AdminOrder) => void,
  notify: (msg: string) => void,
) {
  const [status, setStatus] = useState<string>(order.status);
  const [trackingNumber, setTrackingNumber] = useState(order.tracking?.number ?? '');
  const [trackingCarrier, setTrackingCarrier] = useState(order.tracking?.carrier ?? '');
  const [saving, setSaving] = useState(false);
  const [refunding, setRefunding] = useState(false);
  const [syncing, setSyncing] = useState(false);

  // `refunded` isn't a manually-settable status (it comes from a refund), but an
  // already-refunded order must still show its current state in the dropdown.
  const statusOptions = (ORDER_STATUSES as readonly string[]).includes(order.status)
    ? ORDER_STATUSES
    : ([...ORDER_STATUSES, order.status] as readonly string[]);

  const dirty = status !== order.status
    || trackingNumber !== (order.tracking?.number ?? '')
    || trackingCarrier !== (order.tracking?.carrier ?? '');

  async function save() {
    setSaving(true);
    try {
      const updated = await adminApi.updateOrderStatus(token, order.id, {
        status,
        trackingNumber: trackingNumber || undefined,
        trackingCarrier: trackingCarrier || undefined,
      });
      onUpdated({ ...order, ...updated });
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Update failed');
    } finally {
      setSaving(false);
    }
  }

  const refundableCentsUSD = order.totalCentsUSD - (order.refundedCentsUSD ?? 0);

  async function handleRefund() {
    if (!confirm(`Refund the remaining ${money(refundableCentsUSD)} for order ${order.id}? This issues a real Square refund.`)) return;
    setRefunding(true);
    try {
      const result = await orderService.refund(order.id, token);
      onUpdated({ ...order, ...(result.order as AdminOrder) });
      notify(result.message);
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Refund failed');
    } finally {
      setRefunding(false);
    }
  }

  async function handleSync() {
    setSyncing(true);
    try {
      const result = await orderService.syncWithSquare(order.id, token);
      onUpdated({ ...order, ...(result.order as AdminOrder) });
      notify(result.message);
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Sync failed');
    } finally {
      setSyncing(false);
    }
  }

  const itemCount = order.items.reduce((s, i) => s + i.quantity, 0);
  // Money has been captured for these statuses, so a refund is possible.
  const REFUNDABLE_STATUSES = ['paid', 'shipped', 'delivered'];
  const canRefund = REFUNDABLE_STATUSES.includes(order.status) && refundableCentsUSD > 0;

  return {
    status, setStatus, trackingNumber, setTrackingNumber, trackingCarrier, setTrackingCarrier,
    saving, refunding, syncing, statusOptions, dirty, save, refundableCentsUSD,
    handleRefund, handleSync, itemCount, canRefund,
  };
}

type OrderRowState = ReturnType<typeof useOrderRow>;

// Expanded detail body (Items / Shipping / Fulfillment / Payment). Stacks to a
// single column on mobile and spreads to three columns from `md` up so it fits
// any screen width without horizontal scrolling.
function OrderDetail({ order, s }: { order: AdminOrder; s: OrderRowState }) {
  return (
    <div className="grid md:grid-cols-3 gap-6" onClick={e => e.stopPropagation()}>
      <div>
        <h4 className="text-xs font-semibold uppercase text-gray-400 mb-2">Items</h4>
        <ul className="space-y-1.5 text-sm text-gray-700">
          {order.items.map((it, i) => (
            <li key={i} className="flex justify-between gap-3">
              <span className="min-w-0">
                <span className="block font-medium text-gray-900 truncate">{it.productName ?? 'Unknown product'}</span>
                <span className="block font-mono text-xs text-gray-400 truncate">{it.productId}</span>
              </span>
              <span className="whitespace-nowrap">{it.quantity} × {money(it.unitPriceCentsUSD)}</span>
            </li>
          ))}
        </ul>
      </div>

      <div>
        <h4 className="text-xs font-semibold uppercase text-gray-400 mb-2">Shipping to</h4>
        <div className="text-sm text-gray-700 break-words">
          <p className="font-medium">{order.shipping.name}</p>
          <p>{order.shipping.line1}</p>
          {order.shipping.line2 && <p>{order.shipping.line2}</p>}
          <p>{order.shipping.city}, {order.shipping.state} {order.shipping.zip}</p>
        </div>
        <p className="text-xs text-gray-400 mt-2 break-words">
          {order.userId ? `Account: ${order.accountEmail}` : 'Guest checkout'}
        </p>
      </div>

      <div>
        <h4 className="text-xs font-semibold uppercase text-gray-400 mb-2">Fulfillment</h4>
        <div className="space-y-2">
          <select value={s.status} onChange={e => s.setStatus(e.target.value)}
            className="w-full px-3 py-2 border rounded-lg bg-white text-sm capitalize">
            {s.statusOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}
          </select>
          <input value={s.trackingCarrier} onChange={e => s.setTrackingCarrier(e.target.value)}
            placeholder="Carrier (e.g. USPS)" className="w-full px-3 py-2 border rounded-lg text-sm" />
          <input value={s.trackingNumber} onChange={e => s.setTrackingNumber(e.target.value)}
            placeholder="Tracking number" className="w-full px-3 py-2 border rounded-lg text-sm" />
          <Button size="sm" disabled={!s.dirty || s.saving} onClick={s.save}
            className="w-full flex items-center justify-center gap-2">
            {s.saving && <Loader2 className="w-4 h-4 animate-spin" />} Update order
          </Button>
        </div>

        <h4 className="text-xs font-semibold uppercase text-gray-400 mt-4 mb-2">Payment</h4>
        <div className="text-sm text-gray-700 mb-2">
          {(order.refundedCentsUSD ?? 0) > 0 ? (
            <p className="text-gray-600">
              Refunded {money(order.refundedCentsUSD)} of {money(order.totalCentsUSD)}
              {order.status === 'refunded' ? ' (fully refunded)' : ''}
            </p>
          ) : (
            <p className="text-gray-500">No refunds yet.</p>
          )}
        </div>
        <div className="space-y-2">
          <Button size="sm" variant="outline" disabled={!s.canRefund || s.refunding} onClick={s.handleRefund}
            className="w-full flex items-center justify-center gap-2">
            {s.refunding ? <Loader2 className="w-4 h-4 animate-spin" /> : <RotateCcw className="w-4 h-4" />}
            Refund {s.refundableCentsUSD > 0 ? money(s.refundableCentsUSD) : ''}
          </Button>
          <Button size="sm" variant="outline" disabled={s.syncing} onClick={s.handleSync}
            className="w-full flex items-center justify-center gap-2">
            {s.syncing ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            Sync with Square
          </Button>
          <p className="text-xs text-gray-400">
            Use “Sync with Square” if a refund or charge was done in Square but the order didn&apos;t update here.
          </p>
        </div>
      </div>
    </div>
  );
}

// Desktop layout: a clickable table row that expands into a full-width detail row.
function OrderRow({ order, token, onUpdated, notify }: { order: AdminOrder; token: string; onUpdated: (o: AdminOrder) => void; notify: (msg: string) => void }) {
  const [expanded, setExpanded] = useState(false);
  const s = useOrderRow(order, token, onUpdated, notify);

  return (
    <>
      <tr className="hover:bg-gray-50 cursor-pointer" onClick={() => setExpanded(v => !v)}>
        <td className="px-4 py-3">
          <div className="flex items-center gap-2">
            {expanded ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
            <span className="font-mono text-xs text-gray-700">{order.id}</span>
          </div>
        </td>
        <td className="px-4 py-3">
          <p className="font-medium text-gray-900">{order.customerName || order.accountName || '—'}</p>
          <p className="text-gray-500 text-xs">{order.customerEmail}</p>
        </td>
        <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{new Date(order.createdAt).toLocaleDateString()}</td>
        <td className="px-4 py-3 text-gray-600">{s.itemCount}</td>
        <td className="px-4 py-3 text-gray-900">{money(order.totalCentsUSD)}</td>
        <td className="px-4 py-3">
          <StatusBadge status={order.status} />
        </td>
      </tr>
      {expanded && (
        <tr className="bg-gray-50/60">
          <td colSpan={6} className="px-4 py-5">
            <OrderDetail order={order} s={s} />
          </td>
        </tr>
      )}
    </>
  );
}

// Mobile layout: a self-contained card whose summary and expanded detail both fit
// within the viewport, so no horizontal scrolling is needed to manage an order.
function OrderCard({ order, token, onUpdated, notify }: { order: AdminOrder; token: string; onUpdated: (o: AdminOrder) => void; notify: (msg: string) => void }) {
  const [expanded, setExpanded] = useState(false);
  const s = useOrderRow(order, token, onUpdated, notify);

  return (
    <div className="border-b last:border-b-0">
      <button
        type="button"
        onClick={() => setExpanded(v => !v)}
        className="w-full text-left px-4 py-3 flex items-start gap-2 hover:bg-gray-50"
        aria-expanded={expanded}
      >
        {expanded
          ? <ChevronDown className="w-4 h-4 text-gray-400 mt-0.5 shrink-0" />
          : <ChevronRight className="w-4 h-4 text-gray-400 mt-0.5 shrink-0" />}
        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between gap-2">
            <span className="font-mono text-xs text-gray-700 truncate">{order.id}</span>
            <StatusBadge status={order.status} />
          </div>
          <p className="font-medium text-gray-900 truncate mt-1">{order.customerName || order.accountName || '—'}</p>
          <p className="text-gray-500 text-xs truncate">{order.customerEmail}</p>
          <p className="text-gray-500 text-xs mt-1">
            {new Date(order.createdAt).toLocaleDateString()} · {s.itemCount} item{s.itemCount === 1 ? '' : 's'} · {money(order.totalCentsUSD)}
          </p>
        </div>
      </button>
      {expanded && (
        <div className="px-4 pb-5 pt-1 bg-gray-50/60">
          <OrderDetail order={order} s={s} />
        </div>
      )}
    </div>
  );
}

function OrdersTab({ token, notify }: { token: string; notify: (msg: string) => void }) {
  const [orders, setOrders] = useState<AdminOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | OrderStatus>('all');

  useEffect(() => {
    setLoading(true);
    adminApi.getOrders(token, filter).then(setOrders).catch(console.error).finally(() => setLoading(false));
  }, [token, filter]);

  return (
    <div>
      <div className="flex flex-wrap gap-2 mb-5">
        {(['all', ...ORDER_STATUSES] as const).map(s => (
          <button key={s} onClick={() => setFilter(s)}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium capitalize transition-colors ${
              filter === s ? 'bg-coastal-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>
            {s}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-center py-16"><Loader2 className="w-8 h-8 text-coastal-600 animate-spin mx-auto" /></div>
      ) : (
        <Card className="p-0 overflow-hidden">
          {/* Desktop: full table. */}
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 text-left text-gray-500 uppercase text-xs tracking-wide">
                  <th className="px-4 py-3 font-medium">Order</th>
                  <th className="px-4 py-3 font-medium">Customer</th>
                  <th className="px-4 py-3 font-medium">Date</th>
                  <th className="px-4 py-3 font-medium">Items</th>
                  <th className="px-4 py-3 font-medium">Total</th>
                  <th className="px-4 py-3 font-medium">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {orders.map(o => (
                  <OrderRow key={o.id} order={o} token={token} notify={notify}
                    onUpdated={(u) => setOrders(prev => prev.map(x => (x.id === u.id ? u : x)))} />
                ))}
                {orders.length === 0 && (
                  <tr><td colSpan={6} className="px-4 py-12 text-center text-gray-400">No orders found.</td></tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Mobile: stacked cards that fit the viewport without horizontal scroll. */}
          <div className="md:hidden">
            {orders.map(o => (
              <OrderCard key={o.id} order={o} token={token} notify={notify}
                onUpdated={(u) => setOrders(prev => prev.map(x => (x.id === u.id ? u : x)))} />
            ))}
            {orders.length === 0 && (
              <div className="px-4 py-12 text-center text-gray-400">No orders found.</div>
            )}
          </div>
        </Card>
      )}
    </div>
  );
}

/* ------------------------------- Accounts tab ------------------------------- */

function AccountsTab({ token, onViewCustomer }: { token: string; onViewCustomer: (email: string) => void }) {
  const [customers, setCustomers] = useState<CustomerSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');

  useEffect(() => {
    setLoading(true);
    adminApi.getCustomers(token).then(setCustomers).catch(console.error).finally(() => setLoading(false));
  }, [token]);

  const q = query.trim().toLowerCase();
  const filtered = q
    ? customers.filter(c => c.name.toLowerCase().includes(q) || c.email.toLowerCase().includes(q))
    : customers;

  const registered = customers.filter(c => c.userId).length;

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <p className="text-gray-600 text-sm">
          {customers.length} customer{customers.length !== 1 ? 's' : ''} · {registered} with accounts
        </p>
        <div className="relative">
          <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input value={query} onChange={e => setQuery(e.target.value)}
            placeholder="Search name or email"
            className="pl-9 pr-3 py-2 border rounded-lg text-sm w-64" />
        </div>
      </div>

      {loading ? (
        <div className="text-center py-16"><Loader2 className="w-8 h-8 text-coastal-600 animate-spin mx-auto" /></div>
      ) : (
        <Card className="p-0 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 text-left text-gray-500 uppercase text-xs tracking-wide">
                  <th className="px-4 py-3 font-medium">Customer</th>
                  <th className="px-4 py-3 font-medium">Account</th>
                  <th className="px-4 py-3 font-medium">Signed up</th>
                  <th className="px-4 py-3 font-medium">Orders</th>
                  <th className="px-4 py-3 font-medium">Total spent</th>
                  <th className="px-4 py-3 font-medium">Last order</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {filtered.map(c => (
                  <tr key={c.email} className="hover:bg-gray-50 cursor-pointer" onClick={() => onViewCustomer(c.email)}>
                    <td className="px-4 py-3">
                      <p className="font-medium text-gray-900">{c.name || '—'}</p>
                      <p className="text-gray-500 text-xs">{c.email}</p>
                    </td>
                    <td className="px-4 py-3">
                      {c.userId
                        ? <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-coastal-100 text-coastal-800">Registered</span>
                        : <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">Guest</span>}
                    </td>
                    <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{fmtDate(c.signupAt)}</td>
                    <td className="px-4 py-3 text-gray-700">{c.orderCount}</td>
                    <td className="px-4 py-3 text-gray-900">{money(c.totalSpentCentsUSD)}</td>
                    <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{fmtDate(c.lastOrderAt)}</td>
                  </tr>
                ))}
                {filtered.length === 0 && (
                  <tr><td colSpan={6} className="px-4 py-12 text-center text-gray-400">No customers found.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}

/* ------------------------------- Subscribers tab ------------------------------- */

const SUBSCRIBER_SOURCE_LABELS: Record<SubscriberSummary['source'], string> = {
  where_to_find: 'Where to Find page',
  checkout: 'Checkout',
  account: 'Account',
  unknown: 'Other',
};

function SubscriberStatusBadge({ status }: { status: SubscriberSummary['status'] }) {
  const style = status === 'confirmed' ? 'bg-green-100 text-green-800' : 'bg-gray-200 text-gray-600';
  const label = status === 'confirmed' ? 'Subscribed' : 'Unsubscribed';
  return <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${style}`}>{label}</span>;
}

// Quotes a CSV field per RFC 4180 (wrap in quotes, double embedded quotes).
function csvField(value: string): string {
  return /[",\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;
}

function SubscribersTab({ token, notify }: { token: string; notify: (msg: string) => void }) {
  const [subscribers, setSubscribers] = useState<SubscriberSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState<'all' | 'confirmed' | 'unsubscribed'>('all');

  useEffect(() => {
    setLoading(true);
    adminApi.getSubscribers(token).then(setSubscribers).catch(console.error).finally(() => setLoading(false));
  }, [token]);

  const q = query.trim().toLowerCase();
  const filtered = subscribers
    .filter(s => filter === 'all' || s.status === filter)
    .filter(s => !q || s.email.toLowerCase().includes(q));

  const confirmedCount = subscribers.filter(s => s.status === 'confirmed').length;

  function handleExport() {
    const columns = ['email', 'status', 'source', 'hasAccount', 'createdAt', 'confirmedAt', 'unsubscribedAt'];
    const rows = subscribers.map(s => [
      s.email, s.status, s.source, s.hasAccount ? 'true' : 'false',
      s.createdAt, s.confirmedAt ?? '', s.unsubscribedAt ?? '',
    ]);
    const csv = [columns, ...rows].map(r => r.map(c => csvField(String(c))).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'subscribers-export.csv';
    a.click();
    URL.revokeObjectURL(url);
    notify('Exported subscribers to CSV');
  }

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <p className="text-gray-600 text-sm">
          {subscribers.length} subscriber{subscribers.length !== 1 ? 's' : ''} · {confirmedCount} active
        </p>
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative">
            <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input value={query} onChange={e => setQuery(e.target.value)}
              placeholder="Search email"
              className="pl-9 pr-3 py-2 border rounded-lg text-sm w-56" />
          </div>
          <Button variant="outline" size="sm" disabled={subscribers.length === 0} onClick={handleExport}
            className="flex items-center gap-2">
            <Download className="w-4 h-4" /> Export CSV
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mb-5">
        {(['all', 'confirmed', 'unsubscribed'] as const).map(s => (
          <button key={s} onClick={() => setFilter(s)}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium capitalize transition-colors ${
              filter === s ? 'bg-coastal-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>
            {s === 'confirmed' ? 'Subscribed' : s === 'unsubscribed' ? 'Unsubscribed' : 'All'}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-center py-16"><Loader2 className="w-8 h-8 text-coastal-600 animate-spin mx-auto" /></div>
      ) : (
        <Card className="p-0 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 text-left text-gray-500 uppercase text-xs tracking-wide">
                  <th className="px-4 py-3 font-medium">Email</th>
                  <th className="px-4 py-3 font-medium">Status</th>
                  <th className="px-4 py-3 font-medium">Source</th>
                  <th className="px-4 py-3 font-medium">Account</th>
                  <th className="px-4 py-3 font-medium">Subscribed</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {filtered.map(s => (
                  <tr key={s.email} className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-medium text-gray-900">{s.email}</td>
                    <td className="px-4 py-3"><SubscriberStatusBadge status={s.status} /></td>
                    <td className="px-4 py-3 text-gray-600">{SUBSCRIBER_SOURCE_LABELS[s.source] ?? 'Other'}</td>
                    <td className="px-4 py-3">
                      {s.hasAccount
                        ? <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-coastal-100 text-coastal-800">Registered</span>
                        : <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">Guest</span>}
                    </td>
                    <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{fmtDate(s.confirmedAt ?? s.createdAt)}</td>
                  </tr>
                ))}
                {filtered.length === 0 && (
                  <tr><td colSpan={5} className="px-4 py-12 text-center text-gray-400">No subscribers found.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}

/* ------------------------------- Feedback tab ------------------------------- */

function fmtDateTime(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? '—'
    : d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
}

// Condenses a raw User-Agent string to "Browser on OS" for the metadata line
// (the full string stays available as a tooltip). Order matters: Edge/Opera
// embed "Chrome", and Chrome embeds "Safari".
function summarizeUserAgent(ua: string): string {
  const browser =
    /Edg(e|A|iOS)?\//.test(ua) ? 'Edge' :
    /OPR\/|Opera/.test(ua) ? 'Opera' :
    /CriOS\//.test(ua) ? 'Chrome' :
    /Chrome\//.test(ua) ? 'Chrome' :
    /FxiOS\//.test(ua) ? 'Firefox' :
    /Firefox\//.test(ua) ? 'Firefox' :
    /Safari\//.test(ua) ? 'Safari' :
    'Unknown browser';
  const os =
    /Windows/.test(ua) ? 'Windows' :
    /iPhone|iPad|iPod/.test(ua) ? 'iOS' :
    /Mac OS X|Macintosh/.test(ua) ? 'macOS' :
    /Android/.test(ua) ? 'Android' :
    /Linux/.test(ua) ? 'Linux' :
    'unknown OS';
  return `${browser} on ${os}`;
}

// Submitter context line under each feedback entry: account (if signed in),
// geo location, IP, and a browser/device summary. Fields the server couldn't
// capture are simply omitted; hover the browser summary for the raw
// User-Agent, client hints, and submitting page.
function FeedbackMetaLine({ meta }: { meta: FeedbackEntry['meta'] }) {
  if (!meta) {
    return <p className="text-xs text-gray-400 mt-2">Anonymous · no submitter details captured</p>;
  }
  const who = meta.userName && meta.userEmail
    ? `${meta.userName} (${meta.userEmail})`
    : meta.userEmail || meta.userName || 'Anonymous';
  const location = [meta.geoCity, meta.geoRegion, meta.geoCountry].filter(Boolean).join(', ');
  const isMobile = meta.secChUaMobile === '?1' || /Mobi/.test(meta.userAgent ?? '');
  const browserLabel = meta.userAgent
    ? `${summarizeUserAgent(meta.userAgent)}${isMobile ? ' (mobile)' : ''}`
    : undefined;
  const browserTitle = [
    meta.userAgent,
    meta.secChUa && `Client hints: ${meta.secChUa}`,
    meta.secChUaPlatform && `Platform: ${meta.secChUaPlatform}`,
    meta.referer && `Submitted from: ${meta.referer}`,
  ].filter(Boolean).join('\n');
  // Anonymous visitor ids for telemetry correlation — shown truncated, full
  // values in the tooltip.
  const idsTitle = [
    meta.sessionId && `Session id: ${meta.sessionId}`,
    meta.clientId && `Client id: ${meta.clientId}`,
  ].filter(Boolean).join('\n');
  const idsLabel = meta.sessionId
    ? `session ${meta.sessionId.slice(0, 8)}`
    : meta.clientId
      ? `client ${meta.clientId.slice(0, 8)}`
      : undefined;
  const parts: { label: string; title?: string }[] = [
    { label: who, title: meta.userEmail },
    ...(location ? [{ label: location }] : []),
    ...(meta.ip ? [{ label: meta.ip }] : []),
    ...(browserLabel ? [{ label: browserLabel, title: browserTitle }] : []),
    ...(meta.language ? [{ label: meta.language.split(',')[0] }] : []),
    ...(idsLabel ? [{ label: idsLabel, title: idsTitle }] : []),
  ];
  return (
    <p className="text-xs text-gray-400 mt-2 break-words">
      {parts.map((p, i) => (
        <span key={i} title={p.title}>
          {i > 0 && ' · '}
          {p.label}
        </span>
      ))}
    </p>
  );
}

// Customer feedback stored as S3 text objects, listed newest-first. Pages are
// fetched with S3's native continuation-token pagination: scrolling near the
// bottom (or pressing "Load older feedback") requests the next page with the
// previous page's cursor, so the admin can scroll as far back as she wants
// without the backend ever listing the whole bucket.
function FeedbackTab({ token, notify }: { token: string; notify: (msg: string) => void }) {
  const [entries, setEntries] = useState<FeedbackEntry[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [initialLoading, setInitialLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [reachedEnd, setReachedEnd] = useState(false);
  const [error, setError] = useState('');
  const busyRef = useRef(false); // guards against double-fires from the observer
  const sentinelRef = useRef<HTMLDivElement>(null);

  const loadPage = useCallback(async (cursor: string | null) => {
    if (busyRef.current) return;
    busyRef.current = true;
    setError('');
    if (cursor) setLoadingMore(true);
    try {
      const page = await adminApi.getFeedback(token, cursor);
      setEntries(prev => (cursor ? [...prev, ...page.items] : page.items));
      setNextCursor(page.nextCursor);
      if (page.nextCursor === null) setReachedEnd(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load feedback');
    } finally {
      busyRef.current = false;
      setInitialLoading(false);
      setLoadingMore(false);
    }
  }, [token]);

  useEffect(() => {
    setEntries([]);
    setNextCursor(null);
    setReachedEnd(false);
    setInitialLoading(true);
    loadPage(null);
  }, [loadPage]);

  // Paged scroll: when the sentinel below the list comes into view, fetch the
  // next (older) page. rootMargin prefetches a bit before the hard bottom.
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || nextCursor === null) return;
    const observer = new IntersectionObserver(
      obsEntries => {
        if (obsEntries[0].isIntersecting) loadPage(nextCursor);
      },
      { rootMargin: '300px' },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [nextCursor, loadPage]);

  // Soft delete: the backend moves the S3 file to the archive prefix, so the
  // entry leaves this list but the file itself is preserved.
  async function handleArchive(entry: FeedbackEntry) {
    if (!confirm('Archive this feedback? It disappears from this list, but the file is kept in the storage archive.')) return;
    try {
      await adminApi.archiveFeedback(token, entry.id);
      setEntries(prev => prev.filter(e => e.id !== entry.id));
      notify('Feedback archived');
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Archive failed');
    }
  }

  if (initialLoading) {
    return <div className="text-center py-16"><Loader2 className="w-8 h-8 text-coastal-600 animate-spin mx-auto" /></div>;
  }

  return (
    <div className="max-w-3xl">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <p className="text-gray-600 text-sm">
          {entries.length} entr{entries.length === 1 ? 'y' : 'ies'} loaded{reachedEnd ? ' · end of feedback' : ' · scroll for older'}
        </p>
        <Button variant="outline" size="sm" onClick={() => { setEntries([]); setNextCursor(null); setReachedEnd(false); setInitialLoading(true); loadPage(null); }}
          className="flex items-center gap-2">
          <RefreshCw className="w-4 h-4" /> Refresh
        </Button>
      </div>

      {error && <p className="text-red-600 text-sm mb-4" role="alert">{error}</p>}

      {entries.length === 0 && !error ? (
        <Card className="text-center py-12 text-gray-400">
          No feedback yet. Entries appear here as customers use the Leave Feedback page.
        </Card>
      ) : (
        <div className="space-y-3">
          {entries.map(entry => (
            <Card key={entry.id} className="p-4">
              <div className="flex items-center justify-between gap-2 mb-2">
                <div className="flex items-center gap-2 min-w-0">
                  <MessageSquareHeart className="w-4 h-4 text-coastal-500 flex-shrink-0" />
                  <span className="text-xs text-gray-400">{fmtDateTime(entry.createdAt)}</span>
                </div>
                <button onClick={() => handleArchive(entry)}
                  className="text-amber-600 hover:text-amber-800 p-1 flex-shrink-0"
                  aria-label="Archive feedback" title="Archive (remove from this list, keep the file)">
                  <Archive className="w-4 h-4" />
                </button>
              </div>
              <p className="text-sm text-gray-800 whitespace-pre-wrap break-words">{entry.text}</p>
              <FeedbackMetaLine meta={entry.meta} />
            </Card>
          ))}
        </div>
      )}

      {/* Sentinel that triggers the next page as it scrolls into view. */}
      <div ref={sentinelRef} />

      <div className="py-6 text-center">
        {loadingMore ? (
          <Loader2 className="w-6 h-6 text-coastal-600 animate-spin mx-auto" />
        ) : nextCursor !== null ? (
          <Button variant="outline" size="sm" onClick={() => loadPage(nextCursor)}>
            Load older feedback
          </Button>
        ) : entries.length > 0 ? (
          <p className="text-xs text-gray-400">You&apos;ve reached the oldest feedback.</p>
        ) : null}
      </div>
    </div>
  );
}

/* ------------------------------- Customer detail modal ------------------------------- */

function CustomerDetailModal({ token, email, onClose }: { token: string; email: string; onClose: () => void }) {
  const [detail, setDetail] = useState<CustomerDetail | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    adminApi.getCustomerDetail(token, email).then(setDetail).catch(console.error).finally(() => setLoading(false));
  }, [token, email]);

  const totalSpentCentsUSD = detail?.orders.reduce((s, o) => s + o.totalCentsUSD, 0) ?? 0;

  return (
    <div className="fixed inset-0 z-[60] flex items-start justify-center overflow-y-auto bg-black/50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl my-8">
        <div className="flex items-start justify-between border-b px-6 py-4">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-full bg-coastal-100 flex items-center justify-center">
              <UserCircle className="w-6 h-6 text-coastal-600" />
            </div>
            <div>
              <h2 className="text-xl font-serif font-bold text-coastal-700">{detail?.name || email}</h2>
              <p className="text-sm text-gray-500">{email}</p>
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700" aria-label="Close">
            <X className="w-5 h-5" />
          </button>
        </div>

        {loading ? (
          <div className="text-center py-16"><Loader2 className="w-8 h-8 text-coastal-600 animate-spin mx-auto" /></div>
        ) : detail ? (
          <div className="px-6 py-5 max-h-[72vh] overflow-y-auto">
            <div className="grid sm:grid-cols-3 gap-4 mb-6">
              <div className="rounded-lg border bg-gray-50 px-4 py-3">
                <p className="text-xs uppercase text-gray-400">Account</p>
                <p className="font-medium text-gray-800">
                  {detail.account ? 'Registered' : 'Guest only'}
                </p>
                {detail.account && (
                  <p className="text-xs text-gray-500">Joined {fmtDate(detail.account.createdAt)}</p>
                )}
              </div>
              <div className="rounded-lg border bg-gray-50 px-4 py-3">
                <p className="text-xs uppercase text-gray-400">Orders</p>
                <p className="font-medium text-gray-800">{detail.orders.length}</p>
              </div>
              <div className="rounded-lg border bg-gray-50 px-4 py-3">
                <p className="text-xs uppercase text-gray-400">Lifetime spend</p>
                <p className="font-medium text-gray-800">{money(totalSpentCentsUSD)}</p>
              </div>
            </div>

            {detail.account?.defaultAddress && (
              <div className="mb-6">
                <h4 className="text-xs font-semibold uppercase text-gray-400 mb-2 flex items-center gap-1">
                  <MapPin className="w-3.5 h-3.5" /> Default address
                </h4>
                <div className="text-sm text-gray-700">
                  <p>{detail.account.defaultAddress.line1}</p>
                  {detail.account.defaultAddress.line2 && <p>{detail.account.defaultAddress.line2}</p>}
                  <p>{detail.account.defaultAddress.city}, {detail.account.defaultAddress.state} {detail.account.defaultAddress.zip}</p>
                </div>
              </div>
            )}

            <h4 className="text-xs font-semibold uppercase text-gray-400 mb-3 flex items-center gap-1">
              <ShoppingBag className="w-3.5 h-3.5" /> Order history
            </h4>
            {detail.orders.length === 0 ? (
              <p className="text-gray-400 text-sm py-6 text-center">No orders yet.</p>
            ) : (
              <div className="space-y-3">
                {detail.orders.map(o => (
                  <div key={o.id} className="rounded-lg border px-4 py-3">
                    <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                      <span className="font-mono text-xs text-gray-500">{o.id}</span>
                      <div className="flex items-center gap-3">
                        <StatusBadge status={o.status} />
                        <span className="text-sm text-gray-400">{fmtDate(o.createdAt)}</span>
                        <span className="font-semibold">{money(o.totalCentsUSD)}</span>
                      </div>
                    </div>
                    <ul className="text-sm text-gray-600 space-y-1">
                      {o.items.map((it, i) => (
                        <li key={i} className="flex justify-between gap-3">
                          <span className="min-w-0">
                            <span className="block font-medium text-gray-800 truncate">{it.productName ?? 'Unknown product'}</span>
                            <span className="block font-mono text-xs text-gray-400">{it.productId}</span>
                          </span>
                          <span className="whitespace-nowrap">{it.quantity} × {money(it.unitPriceCentsUSD)}</span>
                        </li>
                      ))}
                    </ul>
                    <p className="text-xs text-gray-400 mt-2">
                      Ship to {o.shipping.city}, {o.shipping.state} {o.shipping.zip}
                      {o.tracking ? ` · ${o.tracking.carrier} ${o.tracking.number}` : ''}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : (
          <p className="text-center py-16 text-gray-400">Could not load customer.</p>
        )}
      </div>
    </div>
  );
}

/* ------------------------------- Settings tab (shipping / tax) ------------------------------- */

function SettingsTab({ token, notify }: { token: string; notify: (msg: string) => void }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const [onlineOrderingEnabled, setOnlineOrderingEnabled] = useState(false);
  const [defaultShippingDollars, setDefaultShippingDollars] = useState(0);
  const [freeShippingEnabled, setFreeShippingEnabled] = useState(true);
  const [freeThresholdDollars, setFreeThresholdDollars] = useState(0);
  const [taxPercent, setTaxPercent] = useState(0);

  useEffect(() => {
    setLoading(true);
    adminApi.getSettings(token)
      .then(s => {
        setOnlineOrderingEnabled(s.onlineOrderingEnabled);
        setDefaultShippingDollars(centsToDollars(s.defaultItemShippingCentsUSD));
        setFreeShippingEnabled(s.freeShippingThresholdCentsUSD != null);
        setFreeThresholdDollars(centsToDollars(s.freeShippingThresholdCentsUSD ?? 0));
        setTaxPercent(Math.round(s.salesTaxRate * 10000) / 100);
      })
      .catch(err => setError(err instanceof Error ? err.message : 'Failed to load settings'))
      .finally(() => setLoading(false));
  }, [token]);

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    setSaving(true);
    try {
      const settings: StoreSettings = {
        onlineOrderingEnabled,
        defaultItemShippingCentsUSD: dollarsToCents(defaultShippingDollars || 0),
        freeShippingThresholdCentsUSD: freeShippingEnabled ? dollarsToCents(freeThresholdDollars || 0) : null,
        salesTaxRate: Math.max(0, Math.min(1, (taxPercent || 0) / 100)),
      };
      await adminApi.updateSettings(token, settings);
      // Nudge always-mounted consumers (e.g. the free-shipping banner in the
      // nav) to refetch the pricing config without a full page reload.
      window.dispatchEvent(new Event(PRICING_CONFIG_UPDATED_EVENT));
      notify('Saved store settings');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return <div className="text-center py-16"><Loader2 className="w-8 h-8 text-coastal-600 animate-spin mx-auto" /></div>;
  }

  return (
    <Card className="max-w-xl">
      <form onSubmit={handleSave} className="p-6 space-y-5">
        <div>
          <h3 className="text-lg font-serif font-bold text-coastal-700">Online ordering</h3>
          <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mt-2">
            <input type="checkbox" checked={onlineOrderingEnabled}
              onChange={e => setOnlineOrderingEnabled(e.target.checked)} />
            Accept online orders
          </label>
          <p className="text-xs text-gray-400 mt-1">
            While off, the storefront shows &ldquo;Coming Soon&rdquo; on every product, the cart and
            checkout are closed, and shoppers are pointed to the Where to Find Us page.
          </p>
        </div>

        <div className="border-t pt-5">
          <h3 className="text-lg font-serif font-bold text-coastal-700">Shipping &amp; tax</h3>
          <p className="text-sm text-gray-500">
            These rules apply to every new order. Each item uses its own shipping override, or this default when it doesn&apos;t set one.
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Default shipping fee (USD per item)</label>
          <input type="number" step="0.01" min="0" value={defaultShippingDollars}
            onChange={e => setDefaultShippingDollars(e.target.valueAsNumber || 0)}
            className="w-full px-3 py-2 border rounded-lg" />
          <p className="text-xs text-gray-400 mt-1">
            Charged per unit for any item that doesn&apos;t set its own shipping override. Free-shipping orders waive it.
          </p>
        </div>

        <div>
          <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
            <input type="checkbox" checked={freeShippingEnabled}
              onChange={e => setFreeShippingEnabled(e.target.checked)} />
            Offer free shipping over a threshold
          </label>
          {freeShippingEnabled && (
            <>
              <label className="block text-sm font-medium text-gray-700 mb-1">Free shipping when subtotal is at least (USD)</label>
              <input type="number" step="0.01" min="0" value={freeThresholdDollars}
                onChange={e => setFreeThresholdDollars(e.target.valueAsNumber || 0)}
                className="w-full px-3 py-2 border rounded-lg" />
              <p className="text-xs text-gray-400 mt-1">e.g. set to 35 to ship free on orders of $35 or more.</p>
            </>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Sales tax (%)</label>
          <input type="number" step="0.01" min="0" max="100" value={taxPercent}
            onChange={e => setTaxPercent(e.target.valueAsNumber || 0)}
            className="w-full px-3 py-2 border rounded-lg" />
        </div>

        {error && <p className="text-red-600 text-sm">{error}</p>}

        <Button type="submit" size="sm" disabled={saving} className="flex items-center gap-2">
          {saving && <Loader2 className="w-4 h-4 animate-spin" />} Save settings
        </Button>
      </form>
    </Card>
  );
}

/* ------------------------------- Page ------------------------------- */

export function AdminPage() {
  const { user, token, loading } = useAuth();
  const [tab, setTab] = useState<'products' | 'orders' | 'accounts' | 'subscribers' | 'feedback' | 'settings'>('products');
  const [toast, setToast] = useState<string>('');
  const [viewCustomer, setViewCustomer] = useState<string | null>(null);

  const notify = useCallback((msg: string) => {
    setToast(msg);
    window.setTimeout(() => setToast(''), 3000);
  }, []);

  if (loading) {
    return (
      <div className="py-24 text-center">
        <Loader2 className="w-8 h-8 text-coastal-600 animate-spin mx-auto mb-3" />
        <p className="text-gray-500">Loading...</p>
      </div>
    );
  }

  if (!user || !user.isAdmin || !token) {
    return (
      <div className="py-24">
        <div className="container-max max-w-md mx-auto text-center">
          <ShieldAlert className="w-14 h-14 text-coastal-300 mx-auto mb-4" />
          <h1 className="text-2xl font-serif font-bold text-coastal-700 mb-2">Admin access only</h1>
          <p className="text-gray-600 mb-6">
            {user ? 'Your account is not authorized to view this page.' : 'Please sign in with an authorized admin account.'}
          </p>
          <Link to={user ? '/' : '/account'}>
            <Button>{user ? 'Back to Home' : 'Sign In'}</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div>
      <section className="bg-cream py-8">
        <div className="container-max">
          <h1 className="text-3xl font-serif font-bold text-coastal-600">Admin Dashboard</h1>
          <p className="text-gray-600">Signed in as {user.email}</p>
        </div>
      </section>

      <section className="py-8">
        <div className="container-max">
          <div className="flex gap-4 mb-8 border-b overflow-x-auto">
            <button onClick={() => setTab('products')}
              className={`shrink-0 whitespace-nowrap pb-3 px-1 font-medium transition-colors flex items-center gap-2 ${tab === 'products' ? 'border-b-2 border-coastal-600 text-coastal-600' : 'text-gray-500 hover:text-gray-700'}`}>
              <Package className="w-4 h-4" /> Products
            </button>
            <button onClick={() => setTab('orders')}
              className={`shrink-0 whitespace-nowrap pb-3 px-1 font-medium transition-colors flex items-center gap-2 ${tab === 'orders' ? 'border-b-2 border-coastal-600 text-coastal-600' : 'text-gray-500 hover:text-gray-700'}`}>
              <ClipboardList className="w-4 h-4" /> Orders
            </button>
            <button onClick={() => setTab('accounts')}
              className={`shrink-0 whitespace-nowrap pb-3 px-1 font-medium transition-colors flex items-center gap-2 ${tab === 'accounts' ? 'border-b-2 border-coastal-600 text-coastal-600' : 'text-gray-500 hover:text-gray-700'}`}>
              <Users className="w-4 h-4" /> Accounts
            </button>
            <button onClick={() => setTab('subscribers')}
              className={`shrink-0 whitespace-nowrap pb-3 px-1 font-medium transition-colors flex items-center gap-2 ${tab === 'subscribers' ? 'border-b-2 border-coastal-600 text-coastal-600' : 'text-gray-500 hover:text-gray-700'}`}>
              <Mail className="w-4 h-4" /> Subscribers
            </button>
            <button onClick={() => setTab('feedback')}
              className={`shrink-0 whitespace-nowrap pb-3 px-1 font-medium transition-colors flex items-center gap-2 ${tab === 'feedback' ? 'border-b-2 border-coastal-600 text-coastal-600' : 'text-gray-500 hover:text-gray-700'}`}>
              <MessageSquareHeart className="w-4 h-4" /> Feedback
            </button>
            <button onClick={() => setTab('settings')}
              className={`shrink-0 whitespace-nowrap pb-3 px-1 font-medium transition-colors flex items-center gap-2 ${tab === 'settings' ? 'border-b-2 border-coastal-600 text-coastal-600' : 'text-gray-500 hover:text-gray-700'}`}>
              <SettingsIcon className="w-4 h-4" /> Settings
            </button>
          </div>

          {tab === 'products' && <ProductsTab token={token} notify={notify} onViewCustomer={setViewCustomer} />}
          {tab === 'orders' && <OrdersTab token={token} notify={notify} />}
          {tab === 'accounts' && <AccountsTab token={token} onViewCustomer={setViewCustomer} />}
          {tab === 'subscribers' && <SubscribersTab token={token} notify={notify} />}
          {tab === 'feedback' && <FeedbackTab token={token} notify={notify} />}
          {tab === 'settings' && <SettingsTab token={token} notify={notify} />}
        </div>
      </section>

      {viewCustomer && (
        <CustomerDetailModal token={token} email={viewCustomer} onClose={() => setViewCustomer(null)} />
      )}

      {toast && (
        <div className="fixed bottom-6 right-6 z-50 bg-coastal-700 text-white px-4 py-3 rounded-lg shadow-lg text-sm">
          {toast}
        </div>
      )}
    </div>
  );
}
